import type { ChangeEventHandler } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { ComboboxItem } from '@screentone/core';
import {
  Box,
  FormHelperText,
  FormLabel,
  Group,
  Input,
  MultiSelect,
  Select,
  Switch,
  Textarea,
  Typography
} from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import {
  Artifact,
  PreferredTimezoneInput,
  PublicationSettingSearchableContentType,
  PublicationSettingUpdateInput
} from 'data/generated/graphql';
import PublicationSettingsActionBar from 'features/publication-settings-common/PublicationSettingsActionBar';
import usePublicationSettingsLock from 'features/publication-settings-common/usePublicationSettingsLock';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings, useSavePublicationSetting } from 'hooks/publication-settings';
import { useAllessehProductAggregations } from 'hooks/useAllessehProductAggregations';
import { useJsonField } from 'hooks/useJsonField';
import { ALT_SUMM_FIELDS } from 'utils/constants';

const contentTypes = Object.values(
  PublicationSettingSearchableContentType
) as PublicationSettingSearchableContentType[];

const toMultiselectItem = (value: string): ComboboxItem => ({ id: value, label: value, value });

const ALT_SUMM_FIELD_OPTIONS = ALT_SUMM_FIELDS.map(toMultiselectItem);
const CONTENT_TYPE_OPTIONS = contentTypes.map(toMultiselectItem);
const ARTIFACT_OPTIONS = Object.values(Artifact).map(toMultiselectItem);

const PublicationSettingsSetup = () => {
  const [currentPublicationSetting, setCurrentPublicationSetting] = useState<PublicationSettingUpdateInput | null>(
    null
  );
  const [hasChanged, setHasChanged] = useState(false);

  const currentProperty = useConvertedProperty();
  const { alertError, alertSuccess } = useAlert();
  const { publishingFragment, lockIsReady, lockLoading, isPageLockingEnabled, handleLock, handleRelease } =
    usePublicationSettingsLock();

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data, isLoading: isLoad, fetchStatus } = usePublicationSettings(publicationSettingsQueryVariables);
  const isLoading = isLoad && fetchStatus !== 'idle';

  // The save puts the saved settings in the cache, so the form reloads from them, with their new version.
  const { mutate, isLoading: isUpdating } = useSavePublicationSetting();
  const { items: productItems, isLoading: isProductsLoading } = useAllessehProductAggregations();

  const selectedProductItems = useMemo<ComboboxItem[]>(
    () =>
      ((currentPublicationSetting?.allowedProducts as string[] | undefined) ?? []).map((p) => ({
        id: p,
        value: p,
        label: p
      })),
    [currentPublicationSetting?.allowedProducts]
  );

  const slugMappings = useJsonField(data?.publicationSetting.slugMappings);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const summaryPriorityByPageType = useJsonField(data?.publicationSetting.summaryPriorityByPageType);
  const originalCollectionMapping = useJsonField(data?.publicationSetting.originalCollectionMapping);

  const jsonFields = useMemo(
    () => [slugMappings, summaryPriorityByPageType, originalCollectionMapping],
    [slugMappings, summaryPriorityByPageType, originalCollectionMapping]
  );

  useEffect(() => {
    if (data) {
      setCurrentPublicationSetting(data.publicationSetting);
    }
  }, [data]);

  const handleSetTags = (
    newTags: string[],
    setting:
      | 'searchableContentTypes'
      | 'enabledArtifacts'
      | 'defaultContentTypes'
      | 'embargoSearchableContentTypes'
      | 'altSummDisabledFields'
  ) => {
    if (currentPublicationSetting == null) {
      return;
    }

    setCurrentPublicationSetting({ ...currentPublicationSetting, [setting]: newTags });
    setHasChanged(true);
  };

  const handleInputStringChange =
    (fieldName: keyof PublicationSettingUpdateInput): ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> =>
    (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (currentPublicationSetting === null) {
        return;
      }
      const { value } = e.target as HTMLInputElement | HTMLTextAreaElement;
      setCurrentPublicationSetting({ ...currentPublicationSetting, [fieldName]: value });
      setHasChanged(true);
    };

  const handleJsonFieldChange =
    (jsonField: { handleChange: (value: string) => void }): ChangeEventHandler<HTMLTextAreaElement> =>
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      jsonField.handleChange((e.target as HTMLTextAreaElement).value);
      setHasChanged(true);
    };

  const handleSelectChange =
    (fieldName: keyof PublicationSettingUpdateInput) => (e: React.FormEvent<HTMLInputElement>) => {
      if (currentPublicationSetting === null) {
        return;
      }
      const { checked } = e.target as HTMLInputElement;
      setCurrentPublicationSetting({ ...currentPublicationSetting, [fieldName]: checked });
      setHasChanged(true);
    };

  const handlePreferredTimezoneChange =
    (fieldName: keyof PreferredTimezoneInput): ChangeEventHandler<HTMLInputElement | HTMLSelectElement> =>
    (e: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (currentPublicationSetting === null) return;

      const { value } = e.target as HTMLInputElement | HTMLSelectElement;
      setCurrentPublicationSetting({
        ...currentPublicationSetting,
        preferredTimezone: { ...currentPublicationSetting.preferredTimezone, [fieldName]: value }
      });
      setHasChanged(true);
    };

  const buildPublicationSettingInput = (): PublicationSettingUpdateInput => ({
    ...currentPublicationSetting!,
    slugMappings: slugMappings.parseValue()?.getJSON(),
    summaryPriorityByPageType: summaryPriorityByPageType.parseValue()?.getJSON(),
    originalCollectionMapping: originalCollectionMapping.parseValue()?.getJSON(),
    allowedProducts:
      ((currentPublicationSetting!.allowedProducts as string[] | null | undefined) ?? []).length > 0
        ? (currentPublicationSetting!.allowedProducts as string[])
        : null
  });

  const handleAllowedProductsChange = (itemsOrFn: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
    if (currentPublicationSetting === null) return;
    const items = typeof itemsOrFn === 'function' ? itemsOrFn(selectedProductItems) : itemsOrFn;
    setCurrentPublicationSetting({
      ...currentPublicationSetting,
      allowedProducts: items.map((item) => item.value as string)
    });
    setHasChanged(true);
  };

  const handleSave = () => {
    if (currentPublicationSetting === null || !currentProperty) {
      return;
    }

    let newPublicationSetting: PublicationSettingUpdateInput;
    try {
      newPublicationSetting = buildPublicationSettingInput();
    } catch (err) {
      alertError(`Cannot save — fix the highlighted JSON field(s) first. (${(err as Error).message})`);
      return;
    }

    mutate(
      {
        publicationSettingUpdateInput: newPublicationSetting
      },
      {
        onSuccess: () => {
          setHasChanged(false);
          jsonFields.forEach((field) => field.clearError());
          alertSuccess('Publication settings saved successfully');
        },
        onError: (error) => {
          alertError(`Failed to save publication settings. ${(error as Error).message}`);
        }
      }
    );
  };

  const { isReadOnly } = publishingFragment;

  const selectedEmbargoSearchableContentTypes = currentPublicationSetting?.embargoSearchableContentTypes;
  const selectedSearchableContentTypes = currentPublicationSetting?.searchableContentTypes;
  const selectedDefaultContentTypes = currentPublicationSetting?.defaultContentTypes;
  const selectedEnabledArtifacts = currentPublicationSetting?.enabledArtifacts;

  const handleEmbargoSearchableContentTypesChange = (
    items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])
  ) => {
    const resolvedItems =
      typeof items === 'function' ? items(selectedEmbargoSearchableContentTypes?.map(toMultiselectItem) ?? []) : items;

    handleSetTags(
      resolvedItems.map((item) => item.value as string),
      'embargoSearchableContentTypes'
    );
  };

  return (
    <Group gap="lg" direction="column">
      {!currentPublicationSetting ? (
        <ResponsiveLoader />
      ) : (
        <>
          <Group.Item fullWidth>
            <PublicationSettingsActionBar
              onSave={handleSave}
              hasChanged={hasChanged}
              isSaving={isUpdating}
              isLoading={isLoading}
              publishingFragment={publishingFragment}
              lockIsReady={lockIsReady}
              isPageLockingEnabled={isPageLockingEnabled}
              lockLoading={lockLoading}
              onLock={handleLock}
              onRelease={handleRelease}
            />
          </Group.Item>

          <Group.Item fullWidth>
            <Box>
              <Box.Title>Setup &amp; Identity</Box.Title>
              <Box.Content>
                <Typography variant="note">
                  Core onboarding configuration for this publication: artifact and content-type access, timezone,
                  routing, embargo behaviour, and brand-specific identity.
                </Typography>
                <Group gap="mlg" direction="column" fullWidth margin={{ top: 'md' }}>
                  <Group gap="xs" direction="column" fullWidth>
                    <MultiSelect
                      label="Enabled artifacts"
                      disabled={isLoading || isReadOnly}
                      items={ARTIFACT_OPTIONS}
                      placeholder="Select artifacts"
                      selectedItems={selectedEnabledArtifacts?.map(toMultiselectItem)}
                      onSelectedItemsChange={(items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
                        const resolvedItems =
                          typeof items === 'function'
                            ? items(selectedEnabledArtifacts?.map(toMultiselectItem) ?? [])
                            : items;
                        handleSetTags(
                          resolvedItems.map((item) => item.value as string),
                          'enabledArtifacts'
                        );
                      }}
                      resetPosition="inside"
                    />
                    <FormHelperText>
                      Which artifact types (collection, layout, banner, newsletter, itp, mobileIssue, sectionPage)
                      editors can create for this brand. Toggling one off hides its entry from the UCS Subnav.
                    </FormHelperText>
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Preferred timezone" labelPosition="top" fullWidth />
                    <FormHelperText>
                      Editors&apos; default timezone for the publish-schedule dialog and dashboard timestamps.
                      Abbreviation is the label shown next to timestamps (e.g. &quot;EST&quot;, &quot;GMT&quot;).
                    </FormHelperText>
                    <Group wrap={false}>
                      <Select
                        defaultValue={currentPublicationSetting.preferredTimezone.timezone}
                        onChange={handlePreferredTimezoneChange('timezone')}
                        disabled={isReadOnly}
                      >
                        {Intl.supportedValuesOf('timeZone').map((tz: string) => (
                          <option value={tz} key={tz}>
                            {tz}
                          </option>
                        ))}
                      </Select>
                      <Input
                        placeholder="Enter the timezone abbreviation"
                        value={currentPublicationSetting.preferredTimezone.timezoneAbbreviation}
                        onChange={handlePreferredTimezoneChange('timezoneAbbreviation')}
                        disabled={isReadOnly}
                      />
                    </Group>
                  </Group>
                  <MultiSelect
                    label="Default Content Types"
                    disabled={isLoading || isReadOnly}
                    items={CONTENT_TYPE_OPTIONS}
                    placeholder="Select default content types"
                    selectedItems={selectedDefaultContentTypes?.map(toMultiselectItem)}
                    onSelectedItemsChange={(items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
                      const resolvedItems =
                        typeof items === 'function'
                          ? items(selectedDefaultContentTypes?.map(toMultiselectItem) ?? [])
                          : items;
                      handleSetTags(
                        resolvedItems.map((item) => item.value as string),
                        'defaultContentTypes'
                      );
                    }}
                    resetPosition="inside"
                  />
                  <MultiSelect
                    label="Allowed Searchable Content Types"
                    disabled={isLoading || isReadOnly}
                    items={CONTENT_TYPE_OPTIONS}
                    placeholder="Select searchable content types"
                    selectedItems={selectedSearchableContentTypes?.map(toMultiselectItem)}
                    onSelectedItemsChange={(items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
                      const resolvedItems =
                        typeof items === 'function'
                          ? items(selectedSearchableContentTypes?.map(toMultiselectItem) ?? [])
                          : items;
                      handleSetTags(
                        resolvedItems.map((item) => item.value as string),
                        'searchableContentTypes'
                      );
                    }}
                    resetPosition="inside"
                  />
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Allowed Products" labelPosition="top" fullWidth />
                    <FormHelperText>
                      Product names whose content is allowed for this publication (replaces contentMap). Required for
                      search by URL and auto-refresh subscriptions.
                    </FormHelperText>
                    <MultiSelect
                      placeholder="Select products"
                      disabled={isLoading || isReadOnly}
                      items={productItems}
                      selectedItems={selectedProductItems}
                      onSelectedItemsChange={handleAllowedProductsChange}
                      status={isProductsLoading ? 'loading' : 'ready'}
                    />
                  </Group>
                  {currentPublicationSetting.disableEmbargo !== null && (
                    <Group gap="xs" direction="column" fullWidth>
                      <FormLabel fullWidth label="Disable Embargo" labelPosition="top" />
                      <FormHelperText>
                        Turn off the embargo gate for this brand — articles publish immediately regardless of embargo
                        timestamp. Off in normal operation; only flip on during incident response.
                      </FormHelperText>
                      <Switch
                        checked={currentPublicationSetting.disableEmbargo}
                        onChange={handleSelectChange('disableEmbargo')}
                        disabled={isReadOnly}
                      />
                    </Group>
                  )}
                  <MultiSelect
                    label="Embargo Searchable Content Types"
                    disabled={isLoading || isReadOnly}
                    items={CONTENT_TYPE_OPTIONS}
                    placeholder="Select embargo searchable content types"
                    selectedItems={selectedEmbargoSearchableContentTypes?.map(toMultiselectItem)}
                    onSelectedItemsChange={handleEmbargoSearchableContentTypesChange}
                    resetPosition="inside"
                  />
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Base Route" labelPosition="top" fullWidth />
                    <FormHelperText>
                      URL prefix for all pages published under this publication (e.g. https://www.wsj.com). Used by the
                      routing layer and by publish previews to construct canonical URLs.
                    </FormHelperText>
                    <Input
                      placeholder="Enter the url prefix for all pages in this publication"
                      value={currentPublicationSetting.baseRoute}
                      onChange={handleInputStringChange('baseRoute')}
                      disabled={isReadOnly}
                    />
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Preview Base Route" labelPosition="top" fullWidth />
                    <FormHelperText>
                      URL prefix used when previewing unpublished changes. Points at the CMS preview environment (not
                      customer-facing).
                    </FormHelperText>
                    <Input
                      placeholder="Enter the preview url prefix for all pages in this publication"
                      value={currentPublicationSetting.previewBaseRoute}
                      onChange={handleInputStringChange('previewBaseRoute')}
                      disabled={isReadOnly}
                    />
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Slug mappings" labelPosition="top" fullWidth />
                    <FormHelperText>
                      Object mapping URL slugs to revision-group keys. Used by the routing layer to find the canonical
                      UCS artifact for a given slug.
                    </FormHelperText>
                    <Textarea
                      placeholder="Enter an object of slugs to revision group key mappings"
                      value={slugMappings.stringValue}
                      onChange={handleJsonFieldChange(slugMappings)}
                      error={!!slugMappings.error}
                      disabled={isReadOnly}
                    />
                    {!!slugMappings.error && <FormHelperText error>{slugMappings.error}</FormHelperText>}
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Original Collection Mapping" labelPosition="top" fullWidth />
                    <FormHelperText>
                      JSON object mapping a page&apos;s Allesseh ID to a different target ID for the original
                      (non-translated) publish. Use this to redirect a page to publish its content into a different
                      collection without a code deployment. Example: {`{ "UCSD_WSJ_MOBILE": "UCSD_WSJ_" }`}
                    </FormHelperText>
                    <Textarea
                      placeholder="Enter the original collection mapping as JSON"
                      value={originalCollectionMapping.stringValue}
                      onChange={handleJsonFieldChange(originalCollectionMapping)}
                      error={!!originalCollectionMapping.error}
                      disabled={isReadOnly}
                    />
                    {!!originalCollectionMapping.error && (
                      <FormHelperText error>{originalCollectionMapping.error}</FormHelperText>
                    )}
                  </Group>
                  {currentPublicationSetting.publicationKey === 'barrons' && (
                    <Group gap="xs" direction="column" fullWidth>
                      <FormLabel label="Topic Tool" labelPosition="top" fullWidth />
                      <FormHelperText>
                        Barrons-only. Points at the Barrons topic-tool endpoint that supplies the topic taxonomy used
                        when tagging articles.
                      </FormHelperText>
                      <Input
                        placeholder="Enter the topic tool"
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        value={currentPublicationSetting.topicTool ?? ''}
                        onChange={handleInputStringChange('topicTool')}
                        disabled={isReadOnly}
                      />
                    </Group>
                  )}
                </Group>
              </Box.Content>
            </Box>
          </Group.Item>

          <Group.Item fullWidth>
            <Typography variant="header2" margin={{ bottom: 'sm' }}>
              Alt-Summ editor config
            </Typography>
            <Box>
              <Box.Content>
                <Typography variant="note">
                  Visibility of the alt-summ editor is gated by the feature flags <em>ShowEditAltSummOption</em> and{' '}
                  <em>CanEditAltSummWithinCuration</em>. The fields below only apply once the editor is enabled.
                </Typography>
                <Group gap="mlg" direction="column" fullWidth margin={{ top: 'md' }}>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Default Alt Summ" labelPosition="top" fullWidth />
                    <FormHelperText>
                      Fallback summary text used when an article has no alt-summ set. Rarely needed — most brands leave
                      blank so the article&apos;s own deck is used.
                    </FormHelperText>
                    <Textarea
                      placeholder="Enter the default alt summ"
                      value={currentPublicationSetting.defaultAltSumm ?? ''}
                      onChange={handleInputStringChange('defaultAltSumm')}
                      disabled={isReadOnly}
                    />
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Summary Priority By Page Type" labelPosition="top" fullWidth />
                    <FormHelperText>
                      JSON keyed by pageType, with an ordered array of alt-summ field names. The first present field
                      wins when rendering the summary on that page type.
                    </FormHelperText>
                    <Textarea
                      placeholder="Enter the summary priority by page type as JSON"
                      value={summaryPriorityByPageType.stringValue}
                      onChange={handleJsonFieldChange(summaryPriorityByPageType)}
                      error={!!summaryPriorityByPageType.error}
                      disabled={isReadOnly}
                    />
                    {!!summaryPriorityByPageType.error && (
                      <FormHelperText error>{summaryPriorityByPageType.error}</FormHelperText>
                    )}
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <MultiSelect
                      label="Disabled Alt-Summ Fields"
                      disabled={isLoading || isReadOnly}
                      items={ALT_SUMM_FIELD_OPTIONS}
                      placeholder="Select fields to hide"
                      selectedItems={(currentPublicationSetting.altSummDisabledFields ?? []).map(toMultiselectItem)}
                      onSelectedItemsChange={(items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
                        const resolvedItems =
                          typeof items === 'function'
                            ? items((currentPublicationSetting.altSummDisabledFields ?? []).map(toMultiselectItem))
                            : items;
                        handleSetTags(
                          resolvedItems.map((item) => item.value as string),
                          'altSummDisabledFields'
                        );
                      }}
                      resetPosition="inside"
                    />
                    <FormHelperText>
                      Fields hidden from the Alt-Summ editor for this brand (e.g. hide &quot;byline&quot; if the mobile
                      app never renders it). Reduces editor cognitive load without touching the underlying data.
                    </FormHelperText>
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel label="Image Manager Snippety Domain" labelPosition="top" fullWidth />
                    <FormHelperText>
                      Base URL that Snippety uses to fetch image assets for this brand. Change only when the Image
                      Manager instance moves.
                    </FormHelperText>
                    <Input
                      placeholder="Enter the IM domain that Snippety will use as base URL"
                      value={currentPublicationSetting.snippetyIMDomain ?? ''}
                      onChange={handleInputStringChange('snippetyIMDomain')}
                      disabled={isReadOnly}
                    />
                  </Group>
                  {currentProperty === 'wsj' && (
                    <>
                      <Group gap="xs" direction="column" fullWidth>
                        <FormLabel label="Video API Snippety Domain" labelPosition="top" fullWidth />
                        <FormHelperText>
                          Video-API base URL used by Snippety when injecting WSJ video embeds. WSJ-only.
                        </FormHelperText>
                        <Input
                          placeholder="Enter the Video API domain that Snippety will use as base URL"
                          value={currentPublicationSetting.snippetyVideoDomain ?? ''}
                          onChange={handleInputStringChange('snippetyVideoDomain')}
                          disabled={isReadOnly}
                        />
                      </Group>
                      <Group gap="xs" direction="column" fullWidth>
                        <FormLabel label="Chartlos API Snippety Domain" labelPosition="top" fullWidth />
                        <FormHelperText>
                          Chartlos base URL used by Snippety when injecting WSJ chart embeds. WSJ-only.
                        </FormHelperText>
                        <Input
                          placeholder="Enter the Chartlos API domain that Snippety will use as base URL"
                          value={currentPublicationSetting.snippetyChartDomain ?? ''}
                          onChange={handleInputStringChange('snippetyChartDomain')}
                          disabled={isReadOnly}
                        />
                      </Group>
                    </>
                  )}
                </Group>
              </Box.Content>
            </Box>
          </Group.Item>
        </>
      )}
    </Group>
  );
};

export default PublicationSettingsSetup;
